// ============================================================
// FindGroupPage.jsx — نسخه نهایی
// صفحه‌ی «جستجوی گروه کلاسی»
// - تمام داده‌ها از دامنه‌ی واقعی سرور با JWT (بدون localStorage)
// - خودکار-به‌روزرسانی هر ۳۰ ثانیه + focus + visibilitychange
// - Polling وضعیت درخواست هر ۵ ثانیه تا تأیید/رد
// - طراحی دقیقاً مطابق HTML دموی کاربر
// ============================================================

import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { getToken } from '../utils/secureStorage';
import * as teamService from './teamService';
import './FindGroupPage.css';

// ============================================================
// ثابت‌ها
// ============================================================
const DEFAULT_AVATAR = '/avatars/avatar34.png';
const ONLINE_THRESHOLD_MS = 45000;
const CACHE_TTL_MS = 10000;
const REFRESH_INTERVAL_MS = 30000;
const POLL_STATUS_MS = 5000;
const DEFAULT_MAX_MEMBERS = 50;
const RECENT_MESSAGES_LIMIT = 200;
const PAGE_SIZE = 20;

const FILTERS = [
  { key: 'all', emoji: '🌟', label: 'همه' },
  { key: 'newest', emoji: '🆕', label: 'جدیدترین' },
  { key: 'popular', emoji: '🔥', label: 'محبوب‌ترین' },
  { key: 'active', emoji: '💬', label: 'فعال‌ترین' },
  { key: 'open', emoji: '🚪', label: 'پذیرش باز' },
];

const SUBJECT_MAP = [
  { keys: ['ریاضی', 'ریاضیات', 'math'], emoji: '📐', c1: '#8b78ea', c2: '#6d28d9' },
  { keys: ['فیزیک', 'physics'], emoji: '⚡', c1: '#22d3ee', c2: '#0891b2' },
  { keys: ['شیمی', 'chemistry'], emoji: '⚗️', c1: '#84cc16', c2: '#65a30d' },
  { keys: ['زیست', 'biology'], emoji: '🧬', c1: '#e879f9', c2: '#a855f7' },
  { keys: ['فارسی', 'ادبیات'], emoji: '📖', c1: '#fd79a8', c2: '#be185d' },
  { keys: ['عربی', 'arabic'], emoji: '📜', c1: '#f59e0b', c2: '#b45309' },
  { keys: ['انگلیسی', 'english'], emoji: '🌐', c1: '#f97316', c2: '#dc2626' },
  { keys: ['قرآن', 'دینی', 'دین', 'معارف'], emoji: '🕌', c1: '#14b8a6', c2: '#0f766e' },
  { keys: ['تاریخ', 'history'], emoji: '🏛️', c1: '#bae6fd', c2: '#0284c7' },
  { keys: ['جغرافیا', 'جغرافی'], emoji: '🗺️', c1: '#a3e635', c2: '#4d7c0f' },
  { keys: ['علوم'], emoji: '🔬', c1: '#06b6d4', c2: '#0e7490' },
  { keys: ['کامپیوتر', 'برنامه', 'کدنویسی'], emoji: '💻', c1: '#3b82f6', c2: '#1d4ed8' },
];

const FALLBACK_PALETTE = [
  { emoji: '📚', c1: '#8b78ea', c2: '#6d28d9' },
  { emoji: '🎓', c1: '#22d3ee', c2: '#0891b2' },
  { emoji: '✏️', c1: '#f59e0b', c2: '#b45309' },
  { emoji: '📝', c1: '#ec4899', c2: '#be185d' },
  { emoji: '🧠', c1: '#a855f7', c2: '#6b21a8' },
];

// ============================================================
// توابع کمکی
// ============================================================
const ensureArray = (v) => (Array.isArray(v) ? v : []);

const idsEqual = (a, b) => {
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

const resolveAvatar = (raw) => {
  if (!raw) return DEFAULT_AVATAR;
  const s = String(raw).trim();
  if (!s) return DEFAULT_AVATAR;
  if (s.startsWith('/') || s.startsWith('http')) return s;
  return DEFAULT_AVATAR;
};

const getFullName = (user) => {
  if (!user) return 'معلم';
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`.trim();
  if (user.name) return user.name;
  if (user.fullName) return user.fullName;
  if (user.username) return user.username;
  if (user.firstName) return user.firstName;
  if (user.lastName) return user.lastName;
  return 'معلم';
};

const fa = (n) => String(n ?? '').replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

const hexToRgba = (hex, alpha) => {
  try {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) throw 0;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch {
    return `rgba(139, 120, 234, ${alpha})`;
  }
};

// ============================================================
// تشخیص API Base — هم‌الگو با authService.js
// ============================================================
function detectApiBase() {
  const env = process.env.REACT_APP_API_URL;
  if (env && /^https?:\/\//i.test(env)) {
    const trimmed = String(env).replace(/\/+$/, '');
    return trimmed.endsWith('/db') ? trimmed : `${trimmed}/db`;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:3001';
    }
  }
  return '/api/db';
}

const API_BASE = detectApiBase();

// ============================================================
// fetch با JWT + timeout
// ============================================================
async function fgFetch(path, options = {}) {
  let token = null;
  try { token = getToken(); } catch { /* ignore */ }

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`;
      try {
        const errBody = await res.json();
        if (errBody?.error) errMsg = errBody.error;
        else if (errBody?.message) errMsg = errBody.message;
      } catch { /* ignore */ }
      throw new Error(errMsg);
    }

    const text = await res.text();
    return text ? JSON.parse(text) : null;
  } catch (e) {
    clearTimeout(timeoutId);
    throw e;
  }
}

// ============================================================
// visual: تشخیص emoji و رنگ بر اساس نام/توضیحات
// ============================================================
function subjectToVisual(team) {
  const text = `${team.name || ''} ${team.description || ''}`.toLowerCase();
  for (const s of SUBJECT_MAP) {
    for (const k of s.keys) {
      if (text.includes(k.toLowerCase())) {
        return { emoji: s.emoji, c1: s.c1, c2: s.c2, glow: hexToRgba(s.c1, 0.4) };
      }
    }
  }
  let h = 0;
  const name = String(team.name || '');
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const fb = FALLBACK_PALETTE[h % FALLBACK_PALETTE.length];
  return { emoji: fb.emoji, c1: fb.c1, c2: fb.c2, glow: hexToRgba(fb.c1, 0.4) };
}

// ============================================================
// Enrichment: داده‌ی خام → گروه‌های غنی
// ============================================================
function enrichGroups({ teams, members, users, messages }, currentUserId) {
  const now = Date.now();
  const cutoff = now - 60 * 60 * 1000;

  const recentMsgCount = {};
  for (const msg of ensureArray(messages)) {
    if (!msg || msg.teamId == null || !msg.timestamp) continue;
    const t = new Date(msg.timestamp).getTime();
    if (!Number.isFinite(t) || t < cutoff) continue;
    const tid = String(msg.teamId);
    recentMsgCount[tid] = (recentMsgCount[tid] || 0) + 1;
  }

  const usersById = {};
  for (const u of ensureArray(users)) {
    if (u && u.id != null) usersById[String(u.id)] = u;
  }

const membersByTeam = {};
const myMemberships = {};
const membershipRank = (s) => (s === 'active' ? 3 : s === 'pending' ? 2 : 1);
for (const m of ensureArray(members)) {
  if (!m || m.teamId == null) continue;
  const tid = String(m.teamId);
  if (!membersByTeam[tid]) membersByTeam[tid] = [];
  membersByTeam[tid].push(m);
  if (idsEqual(m.studentId, currentUserId)) {
    const prev = myMemberships[tid];
    if (!prev || membershipRank(m.status) >= membershipRank(prev.status)) {
      myMemberships[tid] = m;
    }
  }
}

  return ensureArray(teams)
    .filter((t) => t && t.id != null)
    .map((team) => {
      const tid = String(team.id);
      const teacher = usersById[String(team.teacherId)] || null;
      const teamMembers = membersByTeam[tid] || [];
      const activeMembers = teamMembers.filter((m) => m.status === 'active');

      const onlineCount = activeMembers.filter((m) => {
        const u = usersById[String(m.studentId)];
        return u?.lastSeen && now - Number(u.lastSeen) < ONLINE_THRESHOLD_MS;
      }).length;

      const myM = myMemberships[tid] || null;
      const maxMembers = Number(team.maxMembers) || DEFAULT_MAX_MEMBERS;
      const memberCount = activeMembers.length;
      const isFull = memberCount >= maxMembers;
      const allowJoin = team.allowStudentMessaging !== false && !isFull;

      const recent = recentMsgCount[tid] || 0;
      const activity =
        recent >= 10 || onlineCount >= 5 ? 'hot'
        : recent >= 3 || onlineCount >= 2 ? 'warm'
        : 'cold';

      const createdAt = new Date(team.createdAt || 0).getTime();
      const isNew = createdAt > 0 && now - createdAt < 24 * 60 * 60 * 1000;
      const verified = !!teacher && (teacher.role === 'teacher' || memberCount > 0);

      return {
        id: team.id,
        name: team.name || 'گروه بدون نام',
        description: team.description || '',
        image: team.image || null,
        teacherId: team.teacherId,
        teacherName: getFullName(teacher),
        teacherAvatar: resolveAvatar(teacher?.avatar),
        memberCount,
        maxMembers,
        onlineCount,
        recentCount: recent,
        activity,
        isNew,
        createdAt,
        allowJoin,
        isFull,
        verified,
        myStatus: myM?.status || null,
        myMemberId: myM?.id || null,
        joinMode: team.joinMode || 'invite_only',
        visual: subjectToVisual(team),
      };
    })
    .sort((a, b) => b.createdAt - a.createdAt);
}

// ============================================================
// کامپوننت اصلی
// ============================================================
export default function FindGroupPage({
  currentUserId,
  isDarkMode = false,
  setIsDarkMode,
  onJoined,
  onClose,
}) {
  // ---- state اصلی ----
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverOnline, setServerOnline] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState(0);

  // ---- UI state ----
  const [currentSearch, setCurrentSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentFilter, setCurrentFilter] = useState('all');
  const [currentSort, setCurrentSort] = useState('relevant');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // ---- per-group ----
  const [joiningMap, setJoiningMap] = useState({});
  const [myMembershipMap, setMyMembershipMap] = useState({});

  // ---- toasts ----
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  // ---- refs ----
  const mountedRef = useRef(true);
  const pollTimersRef = useRef({});
  const scrollContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const lastFetchedAtRef = useRef(0);

  // ============================================================
  // Toast محلی
  // ============================================================
  const showLocalToast = useCallback((message, type = 'info', duration = 3500) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      if (!mountedRef.current) return;
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  // ============================================================
  // Debounce جستجو
  // ============================================================
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(currentSearch), 300);
    return () => clearTimeout(t);
  }, [currentSearch]);

  // ============================================================
  // Polling تأیید یک گروه
  // ============================================================
const startPolling = useCallback(
  (groupId, expectedMemberId) => {
    if (!currentUserId) return;
    if (pollTimersRef.current[groupId]) return;

    const tick = async () => {
      if (!mountedRef.current) return;
      try {
        const raw = await fgFetch(
          `/teamMembers?studentId=${encodeURIComponent(currentUserId)}`
        );
        const arr = ensureArray(raw);
        const related = arr.filter((m) => idsEqual(m.teamId, groupId));
        const mine =
          related.find((m) => idsEqual(m.id, expectedMemberId)) ||
          related.find((m) => m.status === 'active') ||
          related.find((m) => m.status === 'pending') ||
          related[related.length - 1];

        if (!mine) return;


          if (mine.status === 'active') {
            setMyMembershipMap((prev) => ({
              ...prev,
              [groupId]: { status: 'active', memberId: mine.id },
            }));
            if (pollTimersRef.current[groupId]) {
              clearInterval(pollTimersRef.current[groupId]);
              delete pollTimersRef.current[groupId];
            }
            showLocalToast('🎉 به گروه پیوستید!', 'success');
            setTimeout(() => {
              if (mountedRef.current && typeof onJoined === 'function') {
                onJoined();
              }
            }, 800);
            return;
          }

          if (mine.status === 'rejected') {
            setMyMembershipMap((prev) => ({
              ...prev,
              [groupId]: { status: null },
            }));
            if (pollTimersRef.current[groupId]) {
              clearInterval(pollTimersRef.current[groupId]);
              delete pollTimersRef.current[groupId];
            }
            showLocalToast('⚠️ درخواست شما رد شد', 'warning');
            return;
          }

          setMyMembershipMap((prev) => ({
            ...prev,
            [groupId]: { status: 'pending', memberId: mine.id },
          }));
        } catch { /* network — دوباره تلاش */ }
      };

      pollTimersRef.current[groupId] = setInterval(tick, POLL_STATUS_MS);
      tick();
    },
    [currentUserId, onJoined, showLocalToast]
  );

  // ============================================================
  // بارگذاری داده‌ها
  // ============================================================
  const fetchGroups = useCallback(
    async ({ silent = false } = {}) => {
      if (!currentUserId) return;
      if (!silent) setLoading(true);

      try {
        const [teamsRes, membersRes, usersRes, messagesRes] = await Promise.allSettled([
          fgFetch('/teams'),
          fgFetch('/teamMembers'),
          fgFetch('/users'),
          fgFetch(`/channelMessages?_sort=timestamp&_order=desc&_limit=${RECENT_MESSAGES_LIMIT}`),
        ]);

        if (teamsRes.status === 'rejected') {
          throw new Error('خطا در بارگذاری گروه‌ها');
        }

        const teams = ensureArray(teamsRes.value);
        const members = membersRes.status === 'fulfilled' ? ensureArray(membersRes.value) : [];
        const users = usersRes.status === 'fulfilled' ? ensureArray(usersRes.value) : [];
        const messages = messagesRes.status === 'fulfilled' ? ensureArray(messagesRes.value) : [];

        const enriched = enrichGroups({ teams, members, users, messages }, currentUserId);

        if (!mountedRef.current) return;

        setGroups(enriched);
        setLastFetchedAt(Date.now());
        lastFetchedAtRef.current = Date.now();
        setError(null);
        setServerOnline(true);

        const newMap = {};
enriched.forEach((g) => {
  if (g.myStatus) {
    newMap[g.id] = { status: g.myStatus, memberId: g.myMemberId };
    if (g.myStatus === 'pending') startPolling(g.id, g.myMemberId);
  }
});
        setMyMembershipMap((prev) => ({ ...prev, ...newMap }));
      } catch (e) {
        if (!mountedRef.current) return;
        setError(e.message || 'خطا در بارگذاری');
        setServerOnline(false);
      } finally {
        if (mountedRef.current && !silent) setLoading(false);
      }
    },
    [currentUserId, startPolling]
  );

  // ============================================================
  // Mount
  // ============================================================
  useEffect(() => {
    mountedRef.current = true;
    if (currentUserId) fetchGroups({ silent: false });

    return () => {
      mountedRef.current = false;
      Object.values(pollTimersRef.current).forEach((t) => clearInterval(t));
      pollTimersRef.current = {};
    };
    
  }, [currentUserId]);

  // ============================================================
  // Auto-refresh: interval + focus + visibility
  // ============================================================
  useEffect(() => {
    if (!currentUserId) return;

    const safeRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchedAtRef.current < CACHE_TTL_MS) return;
      fetchGroups({ silent: true });
    };

    const interval = setInterval(safeRefresh, REFRESH_INTERVAL_MS);
    const onFocus = () => safeRefresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') safeRefresh();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentUserId, fetchGroups]);

  // ============================================================
  // Reset scroll و visibleCount
  // ============================================================
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentFilter, currentSort, debouncedSearch]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [currentFilter, currentSort, debouncedSearch]);

  // ============================================================
  // پیوستن
  // ============================================================
  const handleJoin = useCallback(
    async (groupId) => {
      if (!currentUserId) return;
      if (joiningMap[groupId]) return;
      const ms = myMembershipMap[groupId]?.status;
      if (ms === 'pending' || ms === 'active') return;

      // پیدا کردن گروه برای پیام‌های دقیق‌تر
      const group = groups.find((g) => idsEqual(g.id, groupId));
      const mode = group?.joinMode || 'invite_only';

      // اگر بسته است، اصلاً درخواست نفرست
      if (mode === 'closed') {
        showLocalToast('🔒 این گروه بسته است', 'warning');
        return;
      }

      // اگر پر است (client-side pre-check)
      if (group?.isFull) {
        showLocalToast('⚠️ ظرفیت این گروه پر است', 'warning');
        return;
      }

      setJoiningMap((prev) => ({ ...prev, [groupId]: true }));

      try {
        // ۱. خواندن گروه برای اطمینان از joinMode
        let teamData = null;
        try {
          teamData = await fgFetch(`/teams/${encodeURIComponent(groupId)}`);
        } catch { /* از group cached استفاده می‌کنیم */ }

        const serverMode = teamData?.joinMode || mode;
        const maxMembers = Number(teamData?.maxMembers) || 50;

        // ۲. اگر closed شد (شاید از وقتی که لود کردیم تغییر کرده باشد)
        if (serverMode === 'closed') {
          setJoiningMap((prev) => ({ ...prev, [groupId]: false }));
          showLocalToast('🔒 این گروه بسته است', 'warning');
          return;
        }

        // ۳. بررسی عضویت قبلی از سرور
        try {
          const existingRaw = await fgFetch(
            `/teamMembers?teamId=${encodeURIComponent(groupId)}&studentId=${encodeURIComponent(currentUserId)}`
          );
          const existingArr = ensureArray(existingRaw);
          const active = existingArr.find((m) => m && m.status === 'active');
          const pending = existingArr.find((m) => m && m.status === 'pending');

          if (active) {
            setJoiningMap((prev) => ({ ...prev, [groupId]: false }));
            setMyMembershipMap((prev) => ({
              ...prev,
              [groupId]: { status: 'active', memberId: active.id },
            }));
            showLocalToast('✅ شما عضو این گروه هستید', 'info');
            return;
          }
if (pending) {
  setJoiningMap((prev) => ({ ...prev, [groupId]: false }));
  setMyMembershipMap((prev) => ({
    ...prev,
    [groupId]: { status: 'pending', memberId: pending.id },
  }));
  showLocalToast('⏳ درخواست شما در انتظار تأیید است', 'warning');
  startPolling(groupId, pending.id);
  return;
}
        } catch { /* ignore */ }

        // ۴. بررسی ظرفیت از سرور
        try {
          const allRaw = await fgFetch(`/teamMembers?teamId=${encodeURIComponent(groupId)}`);
          const activeCount = ensureArray(allRaw).filter((m) => m && m.status === 'active').length;
          if (activeCount >= maxMembers) {
            setJoiningMap((prev) => ({ ...prev, [groupId]: false }));
            showLocalToast('⚠️ ظرفیت این گروه پر است', 'warning');
            return;
          }
        } catch { /* ignore */ }

        // ۵. ساخت member
        const now = Date.now();
        const isOpen = serverMode === 'open';
        const memberPayload = {
          teamId: groupId,
          studentId: String(currentUserId),
          role: 'member',
          direction: 'student_request',
          mode: serverMode,
          requestedAt: now,
          expiresAt: isOpen ? null : now + 5 * 60 * 1000,
          joinedAt: new Date().toISOString(),
          status: isOpen ? 'active' : 'pending',
        };

        const createdMember = await fgFetch('/teamMembers', {
          method: 'POST',
          body: JSON.stringify(memberPayload),
        });

        if (!mountedRef.current) return;

        // ۶. اگر open بود → عضو شد
        if (isOpen) {
          setMyMembershipMap((prev) => ({
            ...prev,
            [groupId]: { status: 'active', memberId: createdMember?.id },
          }));
          setJoiningMap((prev) => ({ ...prev, [groupId]: false }));
          showLocalToast(
            `🎉 به گروه «${group?.name || 'گروه'}» پیوستید`,
            'success'
          );
          setTimeout(() => {
            if (mountedRef.current && typeof onJoined === 'function') {
              onJoined();
            }
          }, 800);
          return;
        }

        // ۷. حالت invite_only → ساخت پیام
        // خواندن کاربر برای نام و آواتار
        let userInfo = null;
        try {
          userInfo = await fgFetch(`/users/${encodeURIComponent(currentUserId)}`);
        } catch { /* ignore */ }

        let userName = `کاربر ${currentUserId}`;
        if (userInfo) {
          if (userInfo.firstName && userInfo.lastName) userName = `${userInfo.firstName} ${userInfo.lastName}`.trim();
          else if (userInfo.name) userName = userInfo.name;
          else if (userInfo.fullName) userName = userInfo.fullName;
          else if (userInfo.username) userName = userInfo.username;
        }
        const userAvatar = userInfo?.avatar || DEFAULT_AVATAR;

        const messagePayload = {
          id: `msg_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
          teamId: groupId,
          senderId: String(currentUserId),
          type: 'join_request',
          content: '',
          timestamp: new Date().toISOString(),
          edited: false,
          pinned: false,
          isRead: false,
          readBy: [],
          reactions: {},
          reactionTs: {},
          joinRequest: {
            memberId: createdMember?.id,
            userId: String(currentUserId),
            userName,
            userAvatar,
            status: 'pending',
            requestedAt: now,
            expiresAt: now + 5 * 60 * 1000,
            respondedAt: null,
            respondedBy: null,
            respondedByName: null,
          },
        };

        try {
          await fgFetch('/channelMessages', {
            method: 'POST',
            body: JSON.stringify(messagePayload),
          });
        } catch (msgErr) {
          // 🆕 rollback: حذف member چون پیام ساخته نشد
          try {
            await fgFetch(`/teamMembers/${encodeURIComponent(createdMember?.id)}`, {
              method: 'DELETE',
            });
          } catch { /* ignore */ }
          throw new Error('خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.');
        }

        if (!mountedRef.current) return;

setMyMembershipMap((prev) => ({
  ...prev,
  [groupId]: { status: 'pending', memberId: createdMember?.id },
}));
setJoiningMap((prev) => ({ ...prev, [groupId]: false }));
showLocalToast(
  `⏳ درخواست پیوستن به «${group?.name || 'گروه'}» ارسال شد`,
  'success'
);

      startPolling(groupId, createdMember?.id);
      } catch (e) {
        if (!mountedRef.current) return;
        setJoiningMap((prev) => ({ ...prev, [groupId]: false }));
        const code = e?.code || '';
        if (code === 'group_full') {
          showLocalToast('⚠️ ظرفیت گروه پر است', 'warning');
        } else if (code === 'group_closed') {
          showLocalToast('🔒 این گروه بسته است', 'warning');
        } else if (code === 'already_member') {
          showLocalToast('✅ شما قبلاً عضو هستید', 'info');
        } else if (code === 'already_pending') {
          showLocalToast('⏳ درخواست شما در انتظار است', 'warning');
        } else {
          showLocalToast(e.message || 'خطا در ارسال درخواست', 'error');
        }
      }
    },
    [currentUserId, joiningMap, myMembershipMap, groups, showLocalToast, startPolling, onJoined]
  );

  // ============================================================
  // فیلتر و مرتب‌سازی
  // ============================================================
  const filteredGroups = useMemo(() => {
    let list = [...groups];

    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase();
      list = list.filter(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          g.teacherName.toLowerCase().includes(q) ||
          (g.description || '').toLowerCase().includes(q)
      );
    }

    if (currentFilter === 'newest') list = [...list].sort((a, b) => b.createdAt - a.createdAt);
    else if (currentFilter === 'popular') list = [...list].sort((a, b) => b.memberCount - a.memberCount);
    else if (currentFilter === 'active') list = [...list].sort((a, b) => b.onlineCount - a.onlineCount);
    else if (currentFilter === 'open') list = list.filter((g) => g.allowJoin);

    if (currentSort === 'popular') list = [...list].sort((a, b) => b.memberCount - a.memberCount);
    else if (currentSort === 'newest') list = [...list].sort((a, b) => b.createdAt - a.createdAt);
    else if (currentSort === 'active') list = [...list].sort((a, b) => b.onlineCount - a.onlineCount);

    return list;
  }, [groups, debouncedSearch, currentFilter, currentSort]);

  const visibleGroups = useMemo(
    () => filteredGroups.slice(0, visibleCount),
    [filteredGroups, visibleCount]
  );
  const hasMore = visibleGroups.length < filteredGroups.length;

  // ============================================================
  // آمار Hero
  // ============================================================
  const heroStats = useMemo(() => {
    const activeGroups = groups.filter((g) => g.activity !== 'cold').length;
    const teacherSet = new Set();
    let studentTotal = 0;
    groups.forEach((g) => {
      if (g.teacherId != null) teacherSet.add(String(g.teacherId));
      studentTotal += g.memberCount;
    });
    return {
      activeGroups,
      totalStudents: studentTotal,
      totalTeachers: teacherSet.size,
    };
  }, [groups]);

  // ============================================================
  // handlers
  // ============================================================
  const handleBack = useCallback(() => {
    if (typeof onClose === 'function') onClose();
    else showLocalToast('بازگشت', 'info');
  }, [onClose, showLocalToast]);

  const handleToggleTheme = useCallback(() => {
    if (typeof setIsDarkMode === 'function') setIsDarkMode(!isDarkMode);
  }, [setIsDarkMode, isDarkMode]);

  const handleClearSearch = useCallback(() => {
    setCurrentSearch('');
    setDebouncedSearch('');
    searchInputRef.current?.focus();
  }, []);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      setDebouncedSearch(currentSearch);
    }
  }, [currentSearch]);

  // ============================================================
  // render: کارت گروه
  // ============================================================
  const renderCard = (g) => {
    const status = myMembershipMap[g.id]?.status;
    const isJoining = joiningMap[g.id] === true;

    let btnLabel = '➕ پیوستن';
    let btnMod = '';
    let btnDisabled = false;

    if (isJoining) {
      btnLabel = '⋯ در حال پیوستن';
      btnMod = 'fg-joining';
      btnDisabled = true;
    } else if (status === 'pending') {
      btnLabel = '⏳ در انتظار تایید';
      btnMod = 'fg-pending';
      btnDisabled = true;
    } else if (status === 'active') {
      btnLabel = '✓ عضو';
      btnMod = 'fg-active';
      btnDisabled = true;
    } else if (g.joinMode === 'closed') {
      btnLabel = '🔒 بسته';
      btnMod = 'fg-full';
      btnDisabled = true;
    } else if (g.isFull) {
      btnLabel = '⚠️ ظرفیت پر';
      btnMod = 'fg-full';
      btnDisabled = true;
    } else if (g.joinMode === 'open') {
      btnLabel = '➕ پیوستن';
      btnMod = '';
      btnDisabled = false;
    } else {
      // invite_only
      btnLabel = '📩 درخواست عضویت';
      btnMod = '';
      btnDisabled = false;
    }

    const activityLabel =
      g.activity === 'hot' ? '🔥 فعال'
      : g.activity === 'warm' ? '✨ متوسط'
      : '💤 کم‌تحرک';

    const activityClass =
      g.activity === 'hot' ? ''
      : g.activity === 'warm' ? 'fg-warm'
      : 'fg-cold';

    return (
      <div
        key={g.id}
        className="fg-group-card"
        style={{
          '--fg-group-color': g.visual.c1,
          '--fg-group-c1': g.visual.c1,
          '--fg-group-c2': g.visual.c2,
          '--fg-group-glow': g.visual.glow,
        }}
      >
        <div className="fg-group-avatar">
          {g.image ? (
            <img
              src={g.image}
              alt={g.name}
              onError={(e) => {
                if (!e.currentTarget.dataset.fb) {
                  e.currentTarget.dataset.fb = '1';
                  e.currentTarget.style.display = 'none';
                }
              }}
            />
          ) : (
            <span>{g.visual.emoji}</span>
          )}
          <span className="fg-group-avatar-badge">{g.verified ? '✓' : '📚'}</span>
        </div>

        <div className="fg-group-info">
          <div className="fg-group-name">
            <span className="fg-group-name-text">{g.name}</span>
            {g.verified && (
              <span className="fg-group-verified" title="تایید شده">✓</span>
            )}
            {g.isNew && <span className="fg-group-new-badge">🆕</span>}
          </div>
          <div className="fg-group-teacher">
            <span className="fg-group-teacher-icon">👨‍🏫</span>
            <span>{g.teacherName}</span>
          </div>
          <div className="fg-group-meta">
            <span className="fg-meta-item">
              <span className="fg-meta-icon">👥</span>
              <span>{fa(g.memberCount)}/{fa(g.maxMembers)}</span>
            </span>
            <span className="fg-meta-item fg-online-count">
              {fa(g.onlineCount)} آنلاین
            </span>
            <span className={`fg-activity-indicator ${activityClass}`}>
              {activityLabel}
            </span>
            {/* 🆕 badge حالت عضویت */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 9px',
                borderRadius: 10,
                fontSize: '0.72rem',
                fontWeight: 700,
                background:
                  g.joinMode === 'open'
                    ? '#dcfce7'
                    : g.joinMode === 'closed'
                      ? '#f1f5f9'
                      : '#fef3c7',
                color:
                  g.joinMode === 'open'
                    ? '#166534'
                    : g.joinMode === 'closed'
                      ? '#475569'
                      : '#92400e',
                border: '1px solid',
                borderColor:
                  g.joinMode === 'open'
                    ? '#86efac'
                    : g.joinMode === 'closed'
                      ? '#cbd5e1'
                      : '#fcd34d',
              }}
            >
              {g.joinMode === 'open' ? '🚪 باز' : g.joinMode === 'closed' ? '🔒 بسته' : '🎫 دعوت'}
            </span>
          </div>
        </div>

        <button
          type="button"
          className={`fg-join-btn ${btnMod}`}
          onClick={() => handleJoin(g.id)}
          disabled={btnDisabled}
        >
          {btnLabel}
        </button>
      </div>
    );
  };

  // ============================================================
  // render: skeleton
  // ============================================================
  const renderSkeleton = (idx) => (
    <div key={`sk-${idx}`} className="fg-skeleton-card">
      <div className="fg-skel fg-skel-avatar" />
      <div className="fg-skel-info">
        <div className="fg-skel fg-skel-line fg-skel-line--medium" />
        <div className="fg-skel fg-skel-line fg-skel-line--short" />
      </div>
      <div className="fg-skel fg-skel-btn" />
    </div>
  );

  // ============================================================
  // render: main
  // ============================================================
  const showSkeleton = loading && groups.length === 0;
  const showError = !!error && groups.length === 0;
  const showEmpty = !loading && !error && filteredGroups.length === 0;

  return (
    <div
      className={`find-group-page ${isDarkMode ? 'find-group-page--dark' : ''}`}
      ref={scrollContainerRef}
    >
      {/* Aurora */}
      <div className="fg-aurora-bg" aria-hidden="true">
        <div className="fg-aurora fg-aurora-1" />
        <div className="fg-aurora fg-aurora-2" />
        <div className="fg-aurora fg-aurora-3" />
      </div>

      {/* Toasts */}
      <div className="fg-toast-container">
        {toasts.map((t) => {
          const icons = { success: '✅', error: '❌', info: 'ℹ️', warning: '⚠️' };
          return (
            <div key={t.id} className={`fg-toast fg-toast--${t.type}`}>
              <span className="fg-toast-icon">{icons[t.type] || 'ℹ️'}</span>
              <span>{t.message}</span>
            </div>
          );
        })}
      </div>

      {!serverOnline && (
        <div className="fg-offline-banner">
          <span>📡 اتصال به سرور قطع است — داده‌ها ممکن است قدیمی باشند</span>
        </div>
      )}

      <div className="fg-page">
        {/* Header */}
        <header className="fg-header">
          <div className="fg-header-left">
            <button
              type="button"
              className="fg-back-btn"
              onClick={handleBack}
              title="بازگشت"
              aria-label="بازگشت"
            >
              →
            </button>
            <div className="fg-header-title">
              <h1>🔍 جستجوی گروه کلاسی</h1>
              <p>گروه خود را پیدا کنید و به آن بپیوندید</p>
            </div>
          </div>

        </header>

        {/* Hero */}
        <section className="fg-hero">
          <div className="fg-hero-content">
            <span className="fg-hero-icon">🏰</span>
            <h2 className="fg-hero-title">گروه خود را پیدا کنید</h2>
            <p className="fg-hero-subtitle">
              به هر گروهی که می‌خواهید بپیوندید — کافیه اسم گروه یا معلم رو جستجو کنید
            </p>

            <div className="fg-search-box">
              <span className="fg-search-icon">🔍</span>
              <input
                ref={searchInputRef}
                type="text"
                className="fg-search-input"
                placeholder="جستجوی نام گروه یا معلم..."
                autoComplete="off"
                value={currentSearch}
                onChange={(e) => setCurrentSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              {currentSearch.trim().length > 0 && (
                <button
                  type="button"
                  className="fg-search-clear fg-search-clear--visible"
                  onClick={handleClearSearch}
                  title="پاک کردن"
                  aria-label="پاک کردن"
                >
                  ✕
                </button>
              )}
              <button
                type="button"
                className="fg-search-btn"
                onClick={() => setDebouncedSearch(currentSearch)}
              >
                جستجو
              </button>
            </div>

            <div className="fg-hero-stats">
              <div className="fg-stat-item">
                <span className="fg-dot" />
                <span>
                  <strong>{fa(heroStats.activeGroups)}</strong> گروه فعال
                </span>
              </div>
              <div className="fg-stat-item">
                <span>👥</span>
                <span>
                  <strong>{fa(heroStats.totalStudents)}</strong> دانش‌آموز
                </span>
              </div>
              <div className="fg-stat-item">
                <span>👨‍🏫</span>
                <span>
                  <strong>{fa(heroStats.totalTeachers)}</strong> معلم
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Filter tabs */}
        <div className="fg-filter-tabs" role="tablist" aria-label="فیلترها">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`fg-filter-tab ${currentFilter === f.key ? 'fg-filter-tab--active' : ''}`}
              onClick={() => setCurrentFilter(f.key)}
              role="tab"
              aria-selected={currentFilter === f.key}
            >
              <span className="fg-filter-tab-emoji">{f.emoji}</span>
              <span>{f.label}</span>
            </button>
          ))}
        </div>

        {/* Results info */}
        <div className="fg-results-info">
          <span className="fg-results-count">
            <strong>{fa(filteredGroups.length)}</strong> گروه پیدا شد
          </span>
          <select
            className="fg-sort-select"
            value={currentSort}
            onChange={(e) => setCurrentSort(e.target.value)}
            aria-label="مرتب‌سازی"
          >
            <option value="relevant">مرتبط‌ترین</option>
            <option value="popular">محبوب‌ترین</option>
            <option value="newest">جدیدترین</option>
            <option value="active">فعال‌ترین</option>
          </select>
        </div>

        {/* Groups list */}
        <div className="fg-groups-list">
          {showSkeleton && (
            <>
              {[0, 1, 2, 3, 4, 5].map(renderSkeleton)}
            </>
          )}

          {showError && (
            <div className="fg-state-box">
              <span className="fg-state-icon">⚠️</span>
              <h3 className="fg-state-title">خطا در بارگذاری</h3>
              <p className="fg-state-text">{error}</p>
              <button
                type="button"
                className="fg-state-retry"
                onClick={() => fetchGroups({ silent: false })}
              >
                🔄 تلاش مجدد
              </button>
            </div>
          )}

          {showEmpty && (
            <div className="fg-state-box">
              <span className="fg-state-icon">🔍</span>
              <h3 className="fg-state-title">گروهی پیدا نشد</h3>
              <p className="fg-state-text">
                {debouncedSearch.trim()
                  ? `هیچ گروهی با «${debouncedSearch}» پیدا نشد. نام دیگری را امتحان کنید.`
                  : groups.length === 0
                    ? 'هنوز هیچ گروهی در سیستم ساخته نشده است.'
                    : 'در حال حاضر گروهی با این فیلتر وجود ندارد.'}
              </p>
            </div>
          )}

          {!showSkeleton && !showError && visibleGroups.map(renderCard)}
        </div>

        {/* Load more */}
        {hasMore && !showError && (
          <div className="fg-load-more-wrap">
            <button
              type="button"
              className="fg-load-more-btn"
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            >
              <span>⬇</span>
              <span>نمایش گروه‌های بیشتر</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}